import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { io } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getChatMessages, sendChatMessage, getEnrollments } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Send, Users, MessageCircle, Search, Smile, 
  Image as ImageIcon, Paperclip
} from 'lucide-react';

export default function Chat() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [activeRoom, setActiveRoom] = useState('general');
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    loadData();
    connectSocket();
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    loadMessages();
    scrollToBottom();
  }, [activeRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectSocket = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    socketRef.current = io(backendUrl, {
      path: '/ws/chat',
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('message', (data) => {
      if (data.room_id === activeRoom) {
        setMessages(prev => [...prev, data]);
      }
    });
  };

  const loadData = async () => {
    try {
      const enrollRes = await getEnrollments();
      setEnrollments(enrollRes.data || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await getChatMessages({ course_id: activeRoom === 'general' ? null : activeRoom });
      setMessages(response.data?.reverse() || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      // Demo messages
      setMessages([
        { message_id: '1', sender_name: 'Sarah Chen', message: 'Hey everyone! How\'s the digital art assignment going?', created_at: new Date().toISOString() },
        { message_id: '2', sender_name: 'Alex Kim', message: 'Pretty good! Struggling with the color theory part though 😅', created_at: new Date().toISOString() },
        { message_id: '3', sender_name: 'Mike Ross', message: 'The AI tutor helped me a lot with that!', created_at: new Date().toISOString() }
      ]);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const messageText = input.trim();
    setInput('');
    setLoading(true);

    try {
      const messageData = {
        message: messageText,
        course_id: activeRoom === 'general' ? null : activeRoom
      };
      
      await sendChatMessage(messageData);
      
      // Add to local state
      setMessages(prev => [...prev, {
        message_id: Date.now().toString(),
        sender_id: user?.user_id,
        sender_name: user?.name,
        sender_picture: user?.picture,
        message: messageText,
        created_at: new Date().toISOString()
      }]);
      
      // Emit to socket
      if (socketRef.current) {
        socketRef.current.emit('message', {
          room_id: activeRoom,
          sender_name: user?.name,
          message: messageText
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const rooms = [
    { id: 'general', name: 'General', icon: Users },
    ...enrollments.map(e => ({
      id: e.course_id,
      name: e.course?.title?.split(' ').slice(0, 2).join(' ') || 'Course',
      icon: MessageCircle
    }))
  ];

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="chat-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Classmates Chat
          </h1>
          <p className="text-muted-foreground">
            Connect with your peers and discuss courses
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-6 h-[70vh]">
          {/* Sidebar - Rooms */}
          <Card className={`lg:col-span-1 ${cardClass}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Channels</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => setActiveRoom(room.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      activeRoom === room.id
                        ? 'bg-primary/20 text-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <room.icon className="w-5 h-5" />
                    <span className="truncate">{room.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Main Chat */}
          <Card className={`lg:col-span-3 flex flex-col ${cardClass}`}>
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  {activeRoom === 'general' ? (
                    <Users className="w-5 h-5 text-primary" />
                  ) : (
                    <MessageCircle className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold">
                    {rooms.find(r => r.id === activeRoom)?.name || 'Chat'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {messages.length} messages
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((msg, i) => {
                  const isOwn = msg.sender_id === user?.user_id;
                  return (
                    <motion.div
                      key={msg.message_id || i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarImage src={msg.sender_picture} />
                        <AvatarFallback>{msg.sender_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className={`max-w-[70%] ${isOwn ? 'text-right' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{msg.sender_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                        <div className={`inline-block p-3 rounded-2xl ${
                          isOwn
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}>
                          <p className="text-sm">{msg.message}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-border">
              <form onSubmit={handleSend} className="flex gap-2">
                <Button type="button" variant="ghost" size="icon">
                  <Smile className="w-5 h-5 text-muted-foreground" />
                </Button>
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={loading || !input.trim()}
                  className="btn-primary"
                  data-testid="chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
